
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X, Mail, Key, AlertCircle, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toolForm, toolParam } from "@/lib/webmcp/attrs";
import T from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal = ({ isOpen, onClose }: AuthModalProps) => {
  const t = useT();
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  
  const { signIn, signUp, isLoading } = useAuth();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    try {
      await signIn(email, password);
      onClose();
    } catch (err: any) {
      setError(err.message || "Invalid email or password. Please try again.");
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    try {
      await signUp(email, password, name);
      onClose();
    } catch (err: any) {
      setError(err.message || "An error occurred during registration. Please try again.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border border-border/80 shadow-lg">
        <div className="relative">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-byteline-blue/10 to-byteline-indigo/5 pointer-events-none" />
          
          <DialogHeader className="px-6 pt-6 pb-0 relative z-10">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-display">
                {activeTab === "signin" ? "Welcome Back" : "Create Account"}
              </DialogTitle>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full text-foreground/60 hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {activeTab === "signin" 
                ? "Sign in to access your account" 
                : "Join thebyteline's Web3 news & token tracking platform"}
            </p>
          </DialogHeader>

          <div className="p-6 relative z-10">
            <Tabs 
              defaultValue="signin" 
              value={activeTab} 
              onValueChange={(value) => {
                setActiveTab(value as "signin" | "signup");
                setError("");
              }}
              className="w-full"
            >
              <TabsList className="w-full mb-6">
                <TabsTrigger value="signin" className="w-1/2"><T k="auto.sign-in-f2fd" /></TabsTrigger>
                <TabsTrigger value="signup" className="w-1/2"><T k="auto.sign-up-d678" /></TabsTrigger>
              </TabsList>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: activeTab === "signin" ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: activeTab === "signin" ? 20 : -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <TabsContent value="signin" className="mt-0">
                    <form
                      onSubmit={handleSignIn}
                      className="space-y-4"
                      {...toolForm("signInToByteline", "Sign in to a Byteline reader account with an email address and password.")}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="email"><T k="auto.email-ce8a" /></Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder={t("auto.you-example-com-4fca")}
                            className="pl-10"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            {...toolParam("The reader's email address.")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password"><T k="auto.password-dc64" /></Label>
                          <a 
                            href="#" 
                            className="text-xs text-byteline-blue hover:text-byteline-indigo transition-colors"
                          >
                            <T k="auto.forgot-password-8458" />
                          </a>
                        </div>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            placeholder="••••••••"
                            className="pl-10"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            {...toolParam("The reader's account password.")}
                          />
                        </div>
                      </div>
                      
                      {error && (
                        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-2 rounded-md">
                          <AlertCircle size={16} />
                          <span>{error}</span>
                        </div>
                      )}
                      
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? "Signing in..." : "Sign In"}
                      </Button>
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="signup" className="mt-0">
                    <form
                      onSubmit={handleSignUp}
                      className="space-y-4"
                      {...toolForm("createBytelineAccount", "Create a new Byteline reader account with a name, email address and password.")}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="name"><T k="auto.full-name-6300" /></Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input
                            id="name"
                            name="fullName"
                            type="text"
                            autoComplete="name"
                            placeholder={t("auto.john-doe-4c2a")}
                            className="pl-10"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            {...toolParam("The reader's full name.")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="signup-email"><T k="auto.email-ce8a" /></Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input
                            id="signup-email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder={t("auto.you-example-com-4fca")}
                            className="pl-10"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            {...toolParam("The email address for the new account.")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="signup-password"><T k="auto.password-dc64" /></Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input
                            id="signup-password"
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            className="pl-10"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            {...toolParam("A password of at least 8 characters for the new account.")}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          <T k="auto.password-must-be-at-least-8-characters-l-db06" />
                        </p>
                      </div>
                      
                      {error && (
                        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-2 rounded-md">
                          <AlertCircle size={16} />
                          <span>{error}</span>
                        </div>
                      )}
                      
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? "Creating account..." : "Create Account"}
                      </Button>
                    </form>
                  </TabsContent>
                </motion.div>
              </AnimatePresence>
            </Tabs>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                By continuing, you agree to thebyteline's{' '}
                <a href="#" className="text-byteline-blue hover:text-byteline-indigo transition-colors">
                  <T k="auto.terms-of-service-3b6c" />
                </a>{' '}
                and{' '}
                <a href="#" className="text-byteline-blue hover:text-byteline-indigo transition-colors">
                  <T k="auto.privacy-policy-fa2e" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
